import React, { useCallback, useState } from 'react';
import { Dialog } from '@mui/material';
import {
  OVERLAY_STATE_KEYS,
  OverlayStepId,
  useTutorialStore,
} from '../../../application/store/TutorialStore';

interface OnboardingPageOverlayProps {
  stepId: OverlayStepId;
  children: (onDismiss: () => void) => React.ReactNode;
}

/**
 * Full-viewport dimmed host for page onboarding overlays.
 * Shown whenever the account tutorial is still active and this step is unfinished —
 * including when the dashboard is not unlocked yet, and when arriving via the main menu.
 * "Skip" closes for the current visit; navigating back to the page shows it again.
 */
const OnboardingPageOverlay: React.FC<OnboardingPageOverlayProps> = ({
  stepId,
  children,
}) => {
  const flags = OVERLAY_STATE_KEYS[stepId];
  // Subscribe to primitives so dismiss/complete updates always re-render.
  const requested = useTutorialStore((s) => s[flags.requested]);
  const tutorialIncomplete = useTutorialStore((s) => s.completed === false);
  const stepCompleted = useTutorialStore((s) =>
    s.completedSteps.some((step) => step.id === stepId),
  );
  const dismissOverlay = useTutorialStore((s) => s.dismissOverlay);
  const [closedLocally, setClosedLocally] = useState(false);

  const shouldShow =
    !closedLocally &&
    (requested || (tutorialIncomplete && !stepCompleted));

  const onDismiss = useCallback(() => {
    setClosedLocally(true);
    dismissOverlay(stepId);
  }, [dismissOverlay, stepId]);

  return (
    <Dialog open={shouldShow} fullWidth maxWidth="sm">
      {children(onDismiss)}
    </Dialog>
  );
};

export default OnboardingPageOverlay;
